import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AppModal from '../Modals/AppModal';
import Select from '../Select/Select';
import Input from '../Input/Input';
import CommentField from '../CommentField/CommentField';
import { Field, reduxForm } from 'redux-form';
import './AddToDashboardDialog.scss';

const AddToDashboardDialog = ({ open, onClose, alertData, onSubmit, handleSubmit, pristine, submitting, reset, invalid, eventStateOptions = [], eventPriorityOptions = [] }, context) => {
  const { t } = context;

  const onSave = () => {
    handleSubmit((values) => {
      // Event priority and state are already numeric from the select options
      const eventPriority = parseInt(values.event_priority, 10) || 0;
      const eventState = parseInt(values.event_state, 10) || 0;

      const payload = {
        data: {
          Priority: eventPriority,
          EventState: eventState,
          Comment: values.comment || '',
          EventOpenType: 'manual' // Always set to 'manual' when adding via this button
        }
      };

      // Pass both the payload and the alert ID to the action
      onSubmit({ alertId: alertData.id, payload });
      onClose();
      reset();
    })();
  };

  const onCancel = () => {
    reset();
    onClose();
  };

  // Use server options if available, otherwise use empty arrays
  const priorities = eventPriorityOptions;
  const eventStates = eventStateOptions.map(option => ({
    ...option,
    value: option.value.toString() // Convert to string for redux-form
  }));

  return (
    <AppModal
      closeIcon
      open={open}
      onClose={onClose}
      title={t('add_alert_to_dashboard')}
      content={
        <div className="add-to-dashboard-dialog-content">
          <form className="form-horizontal sticky-actions">
            <div className="form-info">
              <p className="info-text">{t('adding_alert_to_dashboard_info')}</p>
            </div>
            
            <fieldset>
              <div className="form-group">
                <label className="col-xs-2 control-label">{t('alert_id')}:</label>
                <div className="col-xs-10">
                  <p className="form-control-static"><strong>{alertData?.id}</strong></p>
                </div>
              </div>
              
              <div className="form-group">
                <label className="col-xs-2 control-label">{t('event_priority')}:</label>
                <div className="col-xs-10">
                  <Field
                    name="event_priority"
                    component={(props) => (
                      <div>
                        <Select
                          {...props}
                          placeholderTitle={t('select')}
                          options={priorities}
                        />
                        {props.meta.touched && props.meta.error && (
                          <div className="text-danger" style={{ marginTop: '5px', fontSize: '12px' }}>
                            {props.meta.error}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="col-xs-2 control-label">{t('event_state')}:</label>
                <div className="col-xs-10">
                  <Field
                    name="event_state"
                    component={(props) => (
                      <div>
                        <Select
                          {...props}
                          placeholderTitle={t('select')}
                          options={eventStates}
                        />
                        {props.meta.touched && props.meta.error && (
                          <div className="text-danger" style={{ marginTop: '5px', fontSize: '12px' }}>
                            {props.meta.error}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="col-xs-2 control-label">{t('comment')}:</label>
                <div className="col-xs-10">
                  <Field
                    name="comment"
                    component="textarea"
                    className="form-control"
                    rows="3"
                    placeholder={t('enter_comment')}
                  />
                </div>
              </div>
            </fieldset>
            
            <div className="form-group">
              <div className="col-xs-12">
                <a 
                  className={`btn btn-success ${invalid || submitting ? 'disabled' : ''}`} 
                  onClick={invalid || submitting ? undefined : onSave}
                  style={{ opacity: invalid || submitting ? 0.6 : 1, cursor: invalid || submitting ? 'not-allowed' : 'pointer' }}
                >
                  {submitting ? t('saving') || 'Saving...' : t('save')}
                </a>
                <a className="btn btn-danger" onClick={onCancel}>
                  {t('close')}
                </a>
              </div>
            </div>
          </form>
        </div>
      }
    />
  );
};

AddToDashboardDialog.contextTypes = {
  t: PropTypes.func.isRequired
};

AddToDashboardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  alertData: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  reset: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
  eventStateOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  eventPriorityOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  }))
};

// Validation function for required fields
const validate = (values, props) => {
  const errors = {};
  
  // Check if event_priority is not selected (undefined, null, or empty string)
  // Note: "0" is a valid value, so we need to check specifically for these cases
  if (values.event_priority === undefined || values.event_priority === null || values.event_priority === '') {
    errors.event_priority = 'Event Priority is required';
  }
  
  // Check if event_state is not selected (undefined, null, or empty string)  
  // Note: "0" is a valid value, so we need to check specifically for these cases
  if (values.event_state === undefined || values.event_state === null || values.event_state === '') {
    errors.event_state = 'Event State is required';
  }
  
  return errors;
};

// Wrap with redux-form
const AddToDashboardDialogForm = reduxForm({
  form: 'addToDashboard',
  enableReinitialize: true,
  destroyOnUnmount: true, // This will clear the form when component unmounts
  validate
})(AddToDashboardDialog);

export default AddToDashboardDialogForm;